/**
 * Avatar — shared rendering component (2026-07-26)
 * ================================================
 *
 * Renders a circular avatar consuming the shared helpers in
 * `lib/avatar.js`. Falls back to deterministic-colour initials when
 * no image is available. Optional `editable` prop shows a small
 * camera badge that triggers a file picker → uploads via
 * `POST /api/users/me/avatar` → surfaces the new URL through the
 * `onUploaded(newUrl)` callback (the caller is responsible for
 * refreshing `user` state).
 *
 * Sizes are in pixels so this can nest inside dropdowns and chat
 * bubbles alike without stretching to fill the parent.
 */
import React, { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import axios from 'axios';
import {
  resolveAvatarUrl, avatarUrlForUser, getInitials, avatarBackgroundFor,
  isKnownAvatarMiss, markAvatarMiss, clearAvatarMiss,
} from '../lib/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Avatar = ({
  userId,
  name,
  avatarUrl,     // from user.avatar_url (relative or absolute)
  avatarVersion, // from user.avatar_version (cache-bust)
  size = 40,
  editable = false,
  onUploaded,
  className = '',
}) => {
  const [imgErrored, setImgErrored] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Prefer the explicit `avatarUrl` (which already contains the
  // version). Otherwise, build one from user id + version — but skip
  // the network round-trip entirely when this id is a known 404
  // (shared negative cache — see lib/avatar.js, Aug 2026).
  const explicit = resolveAvatarUrl(avatarUrl);
  const src = imgErrored
    ? null
    : (explicit || (isKnownAvatarMiss(userId) ? null : avatarUrlForUser(userId, avatarVersion)));

  const initials = getInitials(name);
  const bg = avatarBackgroundFor(userId || name);
  const fontSize = Math.round(size * 0.38);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('حجم الصورة يتجاوز 2MB — يرجى تصغيرها.');
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(
        `${API_URL}/api/users/me/avatar`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setImgErrored(false);
      clearAvatarMiss(userId);
      if (onUploaded) onUploaded(resp.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'فشل رفع الصورة، حاول مرة أخرى.';
      alert(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      data-testid={userId ? `avatar-${userId}` : 'avatar'}
    >
      {src ? (
        <img
          src={src}
          alt={name || 'user'}
          width={size}
          height={size}
          className="rounded-full object-cover"
          style={{ width: size, height: size }}
          onError={() => {
            if (!explicit) markAvatarMiss(userId);
            setImgErrored(true);
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          className="rounded-full text-white font-semibold flex items-center justify-center select-none"
          style={{
            width: size, height: size, background: bg, fontSize,
            letterSpacing: '0.5px',
          }}
        >
          {initials}
        </div>
      )}
      {editable && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="avatar-edit-btn"
            className="absolute -bottom-0.5 -end-0.5 rounded-full bg-white border border-slate-200 shadow-sm text-slate-700 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-50"
            style={{
              width: Math.max(20, Math.round(size * 0.34)),
              height: Math.max(20, Math.round(size * 0.34)),
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="تحديث الصورة الشخصية"
          >
            <Camera size={Math.max(11, Math.round(size * 0.18))} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFile}
            data-testid="avatar-file-input"
          />
        </>
      )}
    </div>
  );
};

export default Avatar;
